import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { ImportTemplates } from '../../constants/appConstants';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import './MeasureComponent.scss';
import { Toast } from 'primereact/toast';
import DataManagementForm from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';
import DataManagementDataTable from '../DataManagement/DataManagementDataTable';
import { createDataManagementQuery, useDataManagementPagination } from '../DataManagement/useDataManagementPagination';

interface Measure {
  measureId: number;
  measureNumber: string;
  measureNumberFile: string;
  providerType: string;
}

const renderMeasureActions = (measure: Measure, onEdit: (measure: Measure) => void, onDelete: (measure: Measure) => void) => (
  <>
    <Button icon="pi pi-pencil" className="p-button-text" style={{ marginRight: 8 }} aria-label={`Edit ${measure.measureNumber}`} onClick={() => onEdit(measure)} />
    <Button icon="pi pi-trash" className="p-button-text p-button-danger" aria-label={`Delete ${measure.measureNumber}`} onClick={() => onDelete(measure)} />
  </>
);

interface MeasuresComponentProps {
  providerTypes: { label: string; value: string }[];
}

const MeasureComponent: React.FC<MeasuresComponentProps> = ({ providerTypes }) => {
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('measure-component-styles');
    return cleanup;
  }, []);

  const { uploadFile } = useDataManagementFileUpload();

  const handleExcelUpload = async (file: File) => {
    setLoading(true);
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.MEASURES);

      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.MEASURES,
      });

      setCurrentPage(1);
      await refresh();
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Measures uploaded successfully', life: 3000 });
    } catch (err: any) {
      let msg = err?.response?.data?.message || err?.message || "Bulk upload failed";
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.measures,
    onUpload: handleExcelUpload,
    title: 'Import Measures from Excel',
    prominentDownloadLink: true,
  });

  const [providerTypeFilter, setProviderTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const query = createDataManagementQuery({ providerType: providerTypeFilter, search });
  const {
    items: measures,
    totalRecords,
    page: currentPage,
    setPage: setCurrentPage,
    loading,
    setLoading,
    error,
    refresh,
  } = useDataManagementPagination<Measure>(ApiEndpoints.GetPagedMeasures, query, { errorPrefix: 'Failed to fetch measures' });
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [measureToDelete, setMeasureToDelete] = useState<any>(null);

  const toast = React.useRef<Toast>(null);
  
  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes : Array.from(new Set(measures.map(m => m.providerType).filter(Boolean))).map(pt => ({ label: pt, value: pt }));

  let dialogHeader = 'Edit Measure';
  if (isAddMode) dialogHeader = 'Add Measure';
  else if (selectedMeasure?.measureNumber) dialogHeader = `Edit Measure - ${selectedMeasure.measureNumber}`;

  return (
    <div style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading}>
      <DataManagementLoader visible={loading} label="Loading measures" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Measure" icon="pi pi-plus" onClick={() => {
            setSelectedMeasure({ measureId: 0, measureNumber: '', measureNumberFile: '', providerType: '' });
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="measure-search-anim">
          <input
            type="text"
            placeholder="Search by Measure Number..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          />
          <span className="pi pi-search" />
        </div>
        <div className="measure-provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => { setProviderTypeFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <DataManagementDataTable value={measures} dataKey="measureId" page={currentPage} totalRecords={totalRecords}
        onPage={setCurrentPage} loading={loading} emptyMessage="No measures found.">
        <Column header="#" headerStyle={{ width: 64, minWidth: 40 }} body={(_measure: Measure, options) => (currentPage - 1) * 10 + options.rowIndex + 1} />
        <Column field="measureNumber" header="Measure Number" body={(measure: Measure) => measure.measureNumber || '-'} />
        <Column field="measureNumberFile" header="Measure Number File" body={(measure: Measure) => measure.measureNumberFile || '-'} />
        <Column field="providerType" header="Provider Type" body={(measure: Measure) => measure.providerType || '-'} />
        <Column header="Actions" body={(measure: Measure) => renderMeasureActions(
          measure,
          currentMeasure => { setSelectedMeasure({ ...currentMeasure }); setIsAddMode(false); setEditDialogVisible(true); },
          currentMeasure => { setMeasureToDelete(currentMeasure); setDeleteDialogVisible(true); },
        )} />
      </DataManagementDataTable>

      {/* Add/Edit Dialog */}
      <Dialog
        header={dialogHeader}
        visible={editDialogVisible}
        style={{ width: '500px', maxWidth: '90vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={async () => {
              const validationErrors = getFormValidationErrors(dataManagementFormConfig.measures, selectedMeasure);
              if (validationErrors.length > 0) {
                toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
                return;
              }
              const payload = normalizeFieldValues(dataManagementFormConfig.measures, selectedMeasure);
              if (isAddMode) {
                try {
                  setLoading(true);
                  await httpService.post(ApiEndpoints.AddMeasure, payload);
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  // Success toast
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Measure added successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              } else if (selectedMeasure) {
                try {
                  setLoading(true);
                  await httpService.put(`${ApiEndpoints.UpdateMeasure}/${selectedMeasure.measureId}`, { ...payload, isActive: selectedMeasure.isActive });
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  // Success toast
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Measure updated successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              }
            }} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <MeasureEditForm editMeasure={selectedMeasure} setEditMeasure={setSelectedMeasure} providerTypesOptions={providerTypeOptions} />
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={async () => {
              if (!measureToDelete) return;
              try {
                setLoading(true);
                await httpService.delete(`${ApiEndpoints.DeleteMeasure}/${measureToDelete.measureId}`);
                await refresh();
                setDeleteDialogVisible(false);
                setMeasureToDelete(null);
              } catch (err: any) {
                const msg = err?.response?.data?.message || err?.message || 'Failed to delete measure';
                toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
              } finally {
                setLoading(false);
              }
            }} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this record{measureToDelete && measureToDelete.measureNumber ? `: ${measureToDelete.measureNumber}` : ''}?
        </div>
      </Dialog>
    </div>
  );
};

interface MeasureEditFormProps {
  editMeasure: any;
  setEditMeasure: (measure: any) => void;
  providerTypesOptions:{
 label: string;
 value: string;
}[];
}

const MeasureEditForm: React.FC<MeasureEditFormProps> = ({ editMeasure, setEditMeasure,providerTypesOptions }) => {
  return (
    <DataManagementForm
      fields={dataManagementFormConfig.measures}
      values={editMeasure}
      onChange={setEditMeasure}
      options={{ providerType: providerTypesOptions }}
    />
  );
};

export default MeasureComponent;